/**
 * Основные функции установки значений в AMO поля
 * Обеспечивают установку значений для различных типов полей
 */

import { findFieldContainer, dispatchEvents, updateCheckboxState, updateMasterCheckboxState } from './amoFieldsUtilsHelpers.js';
import { updateMultiselectMasterElements, updateAmoModel, getAmoFieldById } from './amoFieldsSetterHelpers.js';

const normalizeMultiselectValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return [];
    }

    if (Array.isArray(value)) {
        return value.map(String);
    }

    return [String(value)];
};

const isAmoMultiselectField = (fieldId) => {
    const field = getAmoFieldById(fieldId);
    return field?.TYPE_CODE === 'MULTISELECT' || Number(field?.TYPE_ID) === 4;
};

/**
 * Установка значения AMO поля
 * @param {string|number} fieldId - ID поля
 * @param {*} value - Значение для установки
 * @returns {boolean} Успешность операции
 */
export function setAmoFieldValue(fieldId, value) {
    try {
        console.log(`🔧 Установка значения для поля ${fieldId}:`, value, typeof value);
        
        const isMultiselectByMeta = isAmoMultiselectField(fieldId);
        const isMultiselectByValue = Array.isArray(value);
        const { element: fieldContainer, success, error } = findFieldContainer(fieldId, {
            silent: isMultiselectByMeta || isMultiselectByValue,
        });

        if (!success && !isMultiselectByMeta && !isMultiselectByValue) {
            console.error(error);
            return false;
        }
        
        const isMultiselect = (
            isMultiselectByMeta ||
            isMultiselectByValue ||
            Boolean(fieldContainer?.querySelector('.checkboxes_dropdown'))
        );
        console.log(`Поле ${fieldId} является мультиселектом:`, !!isMultiselect);
        
        if (isMultiselect) {
            console.log(`🎯 Вызов setAmoMultiselectValue для ${fieldId}`);
            return setAmoMultiselectValue(fieldId, value, fieldContainer);
        } else {
            console.log(`📝 Вызов setAmoSimpleFieldValue для ${fieldId}`);
            return setAmoSimpleFieldValue(fieldId, value, fieldContainer);
        }
    } catch (error) {
        console.error(`Ошибка при установке значения AMO поля ${fieldId}:`, error);
        return false;
    }
}

/**
 * Установка значения для мультиселекта AMO
 */
export function setAmoMultiselectValue(fieldId, value, fieldContainer) {
    try {
        console.log(`Установка мультиселекта ${fieldId}, значения:`, value);
        
        const targetIds = normalizeMultiselectValue(value);
        
        console.log('Целевые ID:', targetIds);

        const modelUpdated = updateAmoModel(fieldId, targetIds);

        if (!fieldContainer) {
            console.warn(`Контейнер мультиселекта ${fieldId} не найден, обновлена только модель AMO`);
            return modelUpdated;
        }
        
        const checkboxes = fieldContainer.querySelectorAll(`input.js-item-checkbox[name^="CFV[${fieldId}]"]`);
        
        if (checkboxes.length === 0) {
            console.warn(`Чекбоксы мультиселекта ${fieldId} не найдены, обновлена только модель AMO`);
            return modelUpdated;
        }
        
        console.log(`Найдено ${checkboxes.length} чекбоксов мультиселекта`);
        
        let checkedCount = 0;
        
        checkboxes.forEach(checkbox => {
            const value = checkbox.getAttribute('value');
            const shouldBeChecked = targetIds.includes(value);
            
            if (shouldBeChecked) {
                checkedCount++;
                console.log(`Выбран: ${value}`);
            } else {
                console.log(`Снят: ${value}`);
            }
            
            updateCheckboxState(checkbox, shouldBeChecked);
        });
        
        updateMultiselectMasterElements(fieldContainer, checkedCount, checkboxes.length);

        console.log(`Мультиселект обновлен: ${checkedCount}/${checkboxes.length} значений выбрано`);
        return modelUpdated;
        
    } catch (error) {
        console.error(`Ошибка установки мультиселекта ${fieldId}:`, error);
        return false;
    }
}

/**
 * Установка значения для обычного поля AMO
 */
export function setAmoSimpleFieldValue(fieldId, value, fieldContainer) {
    try {
        const selectors = [
            `input[name="CFV[${fieldId}]"]`,
            'input',
            'select', 
            'textarea'
        ];
        
        let fieldElement = null;
        let usedSelector = '';
        
        for (const selector of selectors) {
            fieldElement = fieldContainer.querySelector(selector);
            if (fieldElement) {
                usedSelector = selector;
                console.log(`Найдено поле через селектор: ${selector}`);
                break;
            }
        }
        
        if (!fieldElement) {
            console.error(`Поле ${fieldId} не найдено внутри контейнера`);
            return false;
        }
        
        fieldElement.value = value;
        
        dispatchEvents(fieldElement, ['input', 'change', 'blur']);
        
        updateAmoModel(fieldId, value);
        
        return true;
        
    } catch (error) {
        console.error(`Ошибка установки простого поля ${fieldId}:`, error);
        return false;
    }
}

/**
 * Очистка всех значений мультиселекта
 * @param {string|number} fieldId - ID поля
 */
export function clearMultiselectField(fieldId) {
    try {
        console.log(`🧹 Очистка мультиселекта ${fieldId}`);
        
        const { element: fieldContainer, success } = findFieldContainer(fieldId, { silent: true });
        if (!success) {
            return updateAmoModel(fieldId, []);
        }
        
        const itemCheckboxes = fieldContainer.querySelectorAll('input.js-item-checkbox');
        itemCheckboxes.forEach(checkbox => {
            updateCheckboxState(checkbox, false);
        });
        
        const masterCheckboxes = fieldContainer.querySelectorAll('.js-master-checkbox');
        masterCheckboxes.forEach(masterCheckbox => {
            updateMasterCheckboxState(masterCheckbox, 'none');
        });
        
        const titleContainer = fieldContainer.querySelector('.checkboxes_dropdown__title');
        if (titleContainer) {
            titleContainer.innerHTML = '';
            const titleItem = document.createElement('div');
            titleItem.className = 'checkboxes_dropdown__title-item';
            titleItem.setAttribute('data-title-before', '');
            titleItem.textContent = 'Выбрать';
            titleContainer.appendChild(titleItem);
        }
        
        const modelUpdated = updateAmoModel(fieldId, []);
        
        console.log(`✅ Мультиселект ${fieldId} очищен`);
        return modelUpdated;
        
    } catch (error) {
        console.error(`Ошибка очистки мультиселекта ${fieldId}:`, error);
        return false;
    }
}
